export interface StreamBufferController {
  push: (chunk: string) => void;
  flush: () => void;
  dispose: () => void;
}

export interface SseParsedEvent {
  data: string;
  eventType?: string;
  isDone: boolean;
}

/**
 * Directly concatenates stream tokens without adding any separator.
 * The backend already includes leading spaces in tokens that start new words.
 */
export const appendStreamText = (current: string, next: string): string => {
  return current + next;
};

/**
 * Batches high-frequency stream chunks into fewer UI updates.
 */
export const createStreamBuffer = (
  onFlush: (value: string) => void,
  flushIntervalMs = 50,
): StreamBufferController => {
  let buffer = '';
  let timer: ReturnType<typeof setTimeout> | null = null;

  const flush = () => {
    if (!buffer) {
      return;
    }

    const value = buffer;
    buffer = '';
    onFlush(value);
  };

  const schedule = () => {
    if (timer) {
      return;
    }

    timer = setTimeout(() => {
      timer = null;
      flush();
    }, flushIntervalMs);
  };

  const push = (chunk: string) => {
    if (!chunk) {
      return;
    }

    buffer = appendStreamText(buffer, chunk);
    schedule();
  };

  const dispose = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }

    flush();
  };

  return {
    push,
    flush,
    dispose,
  };
};

/**
 * Normalizes line endings in a decoded SSE chunk.
 */
export const normalizeSseChunk = (chunk: string): string => {
  return chunk.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
};

/**
 * Splits a buffer into complete SSE frames and remaining partial content.
 */
export const splitSseFrames = (buffer: string): { frames: string[]; rest: string } => {
  const frames: string[] = [];
  let rest = buffer;

  let separatorIndex = rest.indexOf('\n\n');
  while (separatorIndex >= 0) {
    frames.push(rest.slice(0, separatorIndex));
    rest = rest.slice(separatorIndex + 2);
    separatorIndex = rest.indexOf('\n\n');
  }

  return { frames, rest };
};

/**
 * Checks whether a response content-type is SSE.
 */
export const isSseContentType = (contentType?: string | null): boolean => {
  return (contentType || '').toLowerCase().includes('text/event-stream');
};

/**
 * Converts escaped newlines in SSE data tokens to real newlines.
 */
export const unescapeSseData = (value: string): string => {
  return value.replace(/\\n/g, '\n');
};

/**
 * Parses one SSE frame into event/data fields.
 */
export const parseSseEvent = (rawEvent: string): SseParsedEvent => {
  const lines = rawEvent
    .split('\n')
    .filter((line) => line.length > 0);

  const eventType = lines.find((line) => line.startsWith('event:'))?.slice(6).trim();
  const data = unescapeSseData(
    lines
      .filter((line) => line.startsWith('data:'))
      .map((line) => {
        // Strip only the single SSE-format space after "data:"
        // Preserve any additional leading spaces as they are part of the token value
        const value = line.slice(5); // remove "data:"
        return value.startsWith(' ') ? value.slice(1) : value;
      })
      .join(''),
  );

  const isDone = eventType === 'done' || data === '[DONE]';

  return {
    data: isDone ? '' : data,
    eventType,
    isDone,
  };
};
